import React from 'react'

import { Box, Card, RadioButton } from '@factorialco/deprecated-design-system'
import { CompetenciesGetJobRolesDocument } from 'generated/resources/graphql'

import Selector from 'modules/core/components/Selector'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

export type Audience = 'all' | 'jobs'

interface Props {
  onChangeSelector: (option: Audience, roleIds: number[]) => void
  defaultAudience?: Audience
  defaultRoleIds?: number[]
}

export const AudienceSelector: React.FC<Props> = ({
  onChangeSelector,
  defaultRoleIds,
  defaultAudience,
}) => {
  const [selectedRoleIds, setSelectedRoleIds] = React.useState<number[]>(defaultRoleIds || [])
  const [audience, setAudience] = React.useState<Audience>(defaultAudience || 'all')
  const { data: jobRoleData } = useQuery(CompetenciesGetJobRolesDocument, { variables: {} })

  const roles = jobRoleData?.jobCatalog.roles || []

  const options = roles.map((role) => (
    <Selector.Option key={role.id} value={role.id} searchValue={role.name} label={role.name}>
      {role.name}
    </Selector.Option>
  ))

  const onChange = (newAudience: Audience, selectedRoles: number[]) => {
    setSelectedRoleIds(selectedRoles)
    setAudience(newAudience)
    onChangeSelector(newAudience, selectedRoles)
  }

  return (
    <Box gap="s8" paddingTop="s40" maxWidth="s600">
      <Card key="audience-all">
        <Card.Title
          size="small"
          action={
            <RadioButton
              size="medium"
              onChange={(_event) => onChange('all', selectedRoleIds)}
              value="all"
              checked={audience === 'all'}
              aria-label={i18n.t(
                'competencies_v3.new_competency_wizard.audience_step.options.everyone_aria_label'
              )}
            />
          }
        >
          {i18n.t('competencies_v3.new_competency_wizard.audience_step.options.everyone')}
        </Card.Title>
      </Card>
      <Card key="audience-jobs">
        <Card.Title
          size="small"
          action={
            <RadioButton
              size="medium"
              onChange={(_event) => onChange('jobs', selectedRoleIds)}
              value="jobs"
              checked={audience === 'jobs'}
              aria-label={i18n.t(
                'competencies_v3.new_competency_wizard.audience_step.options.jobs_aria_label'
              )}
            />
          }
        >
          {i18n.t('competencies_v3.new_competency_wizard.audience_step.options.jobs')}
        </Card.Title>
        <Card.ExpandableContent expanded={audience === 'jobs'}>
          <Selector.Container
            value={selectedRoleIds}
            searchPlaceholder={i18n.t('common.search')}
            blankLabel={i18n.t('job_catalog.no_jobs_found')}
            // eslint-disable-next-line no-type-assertion/no-type-assertion -- Selector component has no generics
            onChange={(roleIds) => onChange('jobs', roleIds as number[])}
            isMultiSelect
          >
            {options || []}
          </Selector.Container>
        </Card.ExpandableContent>
      </Card>
    </Box>
  )
}
